import { createContainer, type OverrideContainerComponent } from 'react-sweet-state';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { addSentryBreadcrumb } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { type ChatStorePrivateState } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { interactionMetrics } from '../../common/utils/browser-metrics/interactionMetrics';

import type { ChatStoreActions } from './actions';
import { chatStore } from './chatStore';
import { fetchConversationChannelsData } from './fetchConversationChannelsData';
import { hydrateConversationWithExternalParams } from './hydrateConversationWithExternalParams';
import { resetChatStoresForSite } from './resetChatStoresForSite';
import type { ChatStoreAction, ChatStoreContainerProps } from './types';

/**
 * ************************************************************
 * Container
 * ************************************************************
 */
const storeInit =
	(): ChatStoreAction =>
	async (
		{ dispatch, getState, setState },
		{ setChatStatus, skipAutoConversationSelection, siteId },
	) => {
		addSentryBreadcrumb('ChatStoreContainer: storeInit');
		setChatStatus('launching');

		const isSiteSwitchResetEnabled = fg('rovo_chat_site_switch_reset');
		const previousSiteId = getState().hydratedSiteId;
		const isSiteChange =
			isSiteSwitchResetEnabled &&
			Boolean(siteId) &&
			previousSiteId !== undefined &&
			previousSiteId !== siteId;

		if (isSiteSwitchResetEnabled) {
			void Promise.resolve().then(() => {
				setState({ hydratedSiteId: siteId });
			});
		}

		// Init conversation hydrates the chat store with conversation from URL or Storage
		// and fetches conversations list (however, fetching conversations list is performed "in the background")
		// conversations list is fetched to be later on displayed in the sidebar/conversation list to remove/minimize
		// visual delay when user opens the side-navigation; or in full-screen mode to fetch conversations as early as possible;
		const initConversation = async () => {
			if (isSiteChange) {
				await dispatch(resetChatStoresForSite({ siteId }));
			}
			dispatch(
				hydrateConversationWithExternalParams({
					autoSelectConversation: !skipAutoConversationSelection,
				}),
			);
			interactionMetrics.initializeChannels.start();
			await dispatch(
				fetchConversationChannelsData({
					isInitialLoad: true,
				}),
			);
			interactionMetrics.initializeChannels.stop();
		};

		void initConversation();

		addSentryBreadcrumb('ChatStoreContainer: storeInit completed');
	};

const storeInitOnUpdate =
	(): ChatStoreAction<Promise<void>> =>
	async ({ getState, setState, dispatch }, { siteId }) => {
		if (!fg('rovo_chat_site_switch_reset')) {
			return;
		}

		const { hydratedSiteId } = getState();

		if (!siteId || siteId === hydratedSiteId) {
			return;
		}

		await Promise.resolve();
		if (getState().hydratedSiteId === siteId) {
			return;
		}

		setState({ hydratedSiteId: siteId });

		if (hydratedSiteId === undefined) {
			return;
		}

		addSentryBreadcrumb('ChatStoreContainer: site changed, re-initialising chat');
		await dispatch(resetChatStoresForSite({ siteId }));
		await dispatch(storeInit());
	};

const chatStoreContainerOptions = {
	onInit: storeInit,
	onUpdate: storeInitOnUpdate,
};

export const ChatStoreContainer: OverrideContainerComponent<ChatStoreContainerProps> =
	createContainer<ChatStorePrivateState, ChatStoreActions, ChatStoreContainerProps>(
		chatStore,
		chatStoreContainerOptions,
	);
